import { FIGURES_BY_PAGE } from './figure-index.gen.ts'
import type { InternalPage } from './pages.ts'

/** A keypress, narrowed to the fields the page-search shortcut reads. */
export interface PageSearchKey {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
  repeat: boolean
}

/**
 * Whether this platform uses the command key for page search.
 *
 * @param platform - `navigator.platform`, or a stand-in in a test.
 * @returns True on macOS and on iOS.
 */
export function isApplePlatform(
  platform: string = navigator.platform
): boolean {
  return /Mac|iPhone|iPad|iPod/.test(platform)
}

/**
 * The chord the search control shows.
 *
 * @param apple - Whether the reader is on an Apple platform.
 * @returns `⌘K` there, and `Ctrl+K` everywhere else.
 */
export function searchShortcutLabel(apple: boolean): string {
  return apple ? '⌘K' : 'Ctrl+K'
}

/**
 * Whether this keypress opens or closes page search.
 *
 * ⌘K on an Apple platform, Ctrl+K elsewhere. The other modifier does not
 * count, so that platform's own shortcut still runs.
 *
 * @param event - The keypress.
 * @param apple - Whether the reader is on an Apple platform.
 * @returns True when the chord matches.
 */
export function isPageSearchShortcut(
  event: PageSearchKey,
  apple: boolean
): boolean {
  if (event.repeat || event.altKey || event.shiftKey) return false
  if (event.key.toLowerCase() !== 'k') return false
  return apple
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey
}

/**
 * The search fields for one page, separated by newlines to retain their ranks.
 *
 * @param page - The declared page.
 * @param railGroup - The section name the rail shows for this page.
 * @param figures - The column headers, stat tile labels and chart series the page shows.
 * @returns Title, keywords, figures, section, page group, description, and unique id.
 */
export function pageSearchValue(
  page: InternalPage,
  railGroup: string,
  figures: readonly string[] = []
): string {
  // A line break inside a field would shift every later field to the wrong weight.
  return [
    page.title,
    page.keywords?.join(' ') ?? '',
    figures.join(' '),
    railGroup,
    page.group !== railGroup ? (page.group ?? '') : '',
    page.description,
    page.id
  ]
    .map(field => field.replace(/\s+/g, ' '))
    .join('\n')
}

function words(text: string): string[] {
  return text.match(/[\p{L}\p{N}]+/gu) ?? []
}

function titleScore(title: string, query: string): number {
  if (title.startsWith(query)) return 1
  if (words(title).some(word => word.startsWith(query))) return 0.9
  return title.includes(query) ? 0.8 : 0
}

function oneEditAway(query: string, word: string): boolean {
  if (Math.abs(query.length - word.length) > 1) return false
  let index = 0
  while (index < query.length && query[index] === word[index]) index += 1
  if (query.length < word.length)
    return query.slice(index) === word.slice(index + 1)
  if (query.length > word.length)
    return query.slice(index + 1) === word.slice(index)
  if (query.slice(index + 1) === word.slice(index + 1)) return true
  return (
    query[index] === word[index + 1] &&
    query[index + 1] === word[index] &&
    query.slice(index + 2) === word.slice(index + 2)
  )
}

function fieldScore(
  text: string,
  query: string,
  weight: number,
  title = false
): number {
  let score = title
    ? titleScore(text, query)
    : text.includes(query)
      ? weight
      : 0
  if (!/^[\p{L}]{4,}$/u.test(query)) return score
  for (const word of words(text)) {
    // Both words need four letters, or "each" reads as a typo of "ACH".
    if (word.length < 4 || !oneEditAway(query, word)) continue
    const exact = title ? titleScore(text, word) : weight
    score = Math.max(score, exact - 0.05)
  }
  return score
}

/**
 * Rank each query word by its strongest field, requiring every word to match.
 *
 * Title prefixes beat title word starts, then inner matches and keywords.
 * A figure the page shows ranks below keywords and above the section.
 * Sections and page groups share the next rank, above descriptions and ids.
 * The weakest word determines most of the score. Matching every word in the
 * title therefore beats a match across weaker fields.
 * A one-edit typo in a word of at least four letters scores below its exact match.
 * A title starting with the whole query takes the highest score.
 * Plain strings remain searchable as titles.
 *
 * @param value - The fields `pageSearchValue` built, or plain search text.
 * @param search - What the reader typed.
 * @returns A score from 0 to 1; 0 excludes the page.
 */
export function pageSearchScore(value: string, search: string): number {
  return scoreSearch(value, search).score
}

/** The field that explains a result, including a page group as a section. */
export type PageSearchField =
  'title' | 'keywords' | 'figures' | 'section' | 'description' | 'id'

/** One accessible page and the section search shows beside it. */
export interface SearchPage {
  page: InternalPage
  group: string
  /**
   * The figures this page shows. When omitted, search uses the generated index.
   * An empty list means the page shows none.
   */
  figures?: readonly string[]
}

/** A matching page, its palette score, and its weakest query word's best field. */
export interface RankedPage extends SearchPage {
  score: number
  matchedField: PageSearchField
  /** The one figure that matched, or null when no one figure did. */
  matchedFigure: string | null
}

/**
 * The fields after the title, in the order `pageSearchValue` writes them.
 *
 * The title is scored on its own, at weight 1. A new field belongs in this
 * list and in `pageSearchValue`, or its weight and its label both drift.
 */
const SEARCH_FIELDS: readonly { field: PageSearchField; weight: number }[] = [
  { field: 'keywords', weight: 0.6 },
  { field: 'figures', weight: 0.5 },
  { field: 'section', weight: 0.4 },
  { field: 'section', weight: 0.4 },
  { field: 'description', weight: 0.2 },
  { field: 'id', weight: 0.1 }
]

function scoreSearch(
  value: string,
  search: string
): Pick<RankedPage, 'score' | 'matchedField'> {
  const query = search.trim().toLowerCase()
  if (query === '') return { score: 1, matchedField: 'title' }
  const [title = '', ...fields] = value.toLowerCase().split('\n')
  const queryWords = query.split(/\s+/)
  if (title.startsWith(queryWords.join(' ')))
    return { score: 1, matchedField: 'title' }
  const matches = queryWords.map(word => {
    const scores = [
      { field: 'title' as const, score: fieldScore(title, word, 1, true) },
      ...fields.map((field, index) => {
        const named = SEARCH_FIELDS[index]
        return {
          field: named?.field ?? 'title',
          score: fieldScore(field, word, named?.weight ?? 0)
        }
      })
    ]
    const best = scores.reduce((left, right) =>
      right.score > left.score ? right : left
    )
    return { score: best.score, matchedField: best.field }
  })
  const weakest = matches.reduce((a, b) => (a.score <= b.score ? a : b))
  if (weakest.score === 0) return weakest
  const average =
    matches.reduce((sum, match) => sum + match.score, 0) / matches.length
  return {
    score: weakest.score * 0.9 + average * 0.1,
    matchedField: weakest.matchedField
  }
}

/**
 * The figures search ranks for one page.
 *
 * An omitted list uses the generated index. An empty list means the page
 * shows none.
 *
 * @param entry - The page and its optional figure list.
 * @returns The figures to search.
 */
export function figuresFor(entry: SearchPage): readonly string[] {
  return entry.figures ?? FIGURES_BY_PAGE[entry.page.id] ?? []
}

/**
 * Rank accessible pages with the same scores and stable ties as the palette.
 *
 * @param pages - The caller's accessible pages in palette order.
 * @param query - What the reader typed.
 * @returns Matching pages, highest score first, without changing the input.
 */
export function rankPages(
  pages: readonly SearchPage[],
  query: string
): RankedPage[] {
  const ranked = pages
    .map(entry => {
      const figures = figuresFor(entry)
      return {
        ...entry,
        ...scoreSearch(
          pageSearchValue(entry.page, entry.group, figures),
          query
        ),
        matchedFigure: matchingFigure(figures, query)
      }
    })
    .filter(entry => entry.score > 0)
  ranked.sort((a, b) => b.score - a.score)
  return ranked
}

/**
 * The figure name to show beside a page, when one figure matches the query.
 *
 * Every query word has to match that one figure. Words split across two
 * figures still find the page, and then the section name stays beside it.
 * A tie goes to the shorter name, then to code-point order.
 *
 * @param figures - The figures the page shows.
 * @param search - What the reader typed.
 * @returns The figure's own casing, or null when no one figure matches.
 */
export function matchingFigure(
  figures: readonly string[],
  search: string
): string | null {
  const query = search.trim().toLowerCase()
  if (query === '') return null
  const queryWords = query.split(/\s+/)
  let best: { name: string; score: number } | null = null
  for (const figure of figures) {
    const text = figure.replace(/\s+/g, ' ').toLowerCase()
    const scores = queryWords.map(word => fieldScore(text, word, 1))
    if (scores.includes(0)) continue
    const average =
      scores.reduce((sum, score) => sum + score, 0) / scores.length
    const score = Math.min(...scores) * 0.9 + average * 0.1
    const shorter = best !== null && figure.length < best.name.length
    const earlier =
      best !== null && figure.length === best.name.length && figure < best.name
    if (
      best === null ||
      score > best.score ||
      (score === best.score && (shorter || earlier))
    ) {
      best = { name: figure, score }
    }
  }
  return best?.name ?? null
}
